import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiErr, fileUrl, downloadFile } from "../lib/api";
import VerticalVideo from "./VerticalVideo";
import {
  Sparkles, Loader2, Wand2, Clapperboard, Film, Download, RotateCcw,
  Captions, Music2, Check, Pencil, ArrowRight, AlertTriangle,
} from "lucide-react";

const STAGES = ["Drafting", "Rendering", "Stitching", "Done"];

function stageIndex(status) {
  if (status === "draft") return 0;
  if (status === "queued" || status === "rendering" || status === "regenerating") return 1;
  if (status === "stitching") return 2;
  if (status === "done") return 3;
  return 0;
}

export default function NewStory({ cast, music, models, refreshStories, goEpisodes }) {
  const [step, setStep] = useState("setup"); // setup | review
  const [story, setStory] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [rendering, setRendering] = useState(false);
  const pollRef = useRef(null);

  // form
  const [premise, setPremise] = useState("");
  const [numBeats, setNumBeats] = useState(5);
  const [model, setModel] = useState("claude-sonnet-5");
  const [selectedCast, setSelectedCast] = useState([]);

  const toggleCast = (id) =>
    setSelectedCast((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const draft = async () => {
    if (!premise.trim()) { toast.error("Type a one-line premise first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/story/script", {
        premise, num_beats: numBeats, model, cast_ids: selectedCast,
      });
      setStory(data);
      setStep("review");
      toast.success("Script drafted — review & tweak before rendering");
    } catch (e) {
      toast.error(apiErr(e, "Script generation failed"));
    } finally {
      setDrafting(false);
    }
  };

  const updateStory = async (patch) => {
    const { data } = await api.put(`/story/${story.id}`, patch);
    setStory(data);
    return data;
  };

  const editBeat = (idx, field, value) => {
    setStory((s) => {
      const beats = s.beats.map((b, i) => (i === idx ? { ...b, [field]: value } : b));
      return { ...s, beats };
    });
  };

  const saveBeats = async () => {
    try {
      await updateStory({ title: story.title, hook: story.hook, beats: story.beats });
      toast.success("Script saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const startRender = async () => {
    try {
      await updateStory({
        title: story.title, hook: story.hook, beats: story.beats,
        captions: story.captions, music_id: story.music_id,
      });
      await api.post(`/story/${story.id}/render`);
      setRendering(true);
      toast.success("Rendering started — this takes a few minutes");
      poll();
    } catch (e) { toast.error(apiErr(e, "Could not start render")); }
  };

  const regenerateClip = async (idx) => {
    try {
      await api.post(`/story/${story.id}/clip/${idx}/regenerate`);
      setStory((s) => ({ ...s, status: "regenerating", regen_index: idx, stage: `Re-rolling clip ${idx + 1}` }));
      setRendering(true);
      toast.success(`Re-rolling clip ${idx + 1} — the rest stays as is`);
      poll();
    } catch (e) { toast.error(apiErr(e, "Could not re-roll clip")); }
  };

  const poll = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/story/${story.id}`);
        setStory(data);
        if (["done", "failed"].includes(data.status)) {
          clearInterval(pollRef.current);
          setRendering(false);
          refreshStories();
          if (data.status === "done") toast.success("Episode ready!");
          if (data.status === "failed") toast.error("Render failed: " + (data.error || ""));
        }
      } catch (e) {}
    }, 3000);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const reset = () => {
    clearInterval(pollRef.current);
    setStory(null); setStep("setup"); setRendering(false);
    setPremise(""); setSelectedCast([]);
  };

  // ---------- SETUP ----------
  if (step === "setup") {
    return (
      <div className="max-w-3xl">
        <h2 className="font-display text-2xl sm:text-3xl font-bold">New Story</h2>
        <p className="text-sm text-slate-400 mt-1 mb-6">One line in. A multi-clip Pixar-style love story out.</p>

        <div className="glass rounded-3xl p-6 sm:p-8 space-y-6">
          <div>
            <label className="text-sm font-semibold mb-2 block">Premise</label>
            <textarea
              data-testid="premise-input"
              value={premise} onChange={(e) => setPremise(e.target.value)} rows={2}
              placeholder="a shy strawberry falls for a confident mango"
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#FF3B60] transition-colors resize-none"
            />
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block">Cast <span className="text-slate-500 font-normal">(optional — reuse your stars)</span></label>
            {cast.length === 0 ? (
              <p className="text-xs text-slate-500">No saved characters yet. The AI will invent fruit characters, or add some in Cast Library.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {cast.map((c) => {
                  const on = selectedCast.includes(c.id);
                  return (
                    <button
                      key={c.id} onClick={() => toggleCast(c.id)}
                      data-testid={`select-cast-${c.id}`}
                      className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border transition-all ${on ? "border-[#FF3B60] bg-[#FF3B60]/15" : "border-white/10 bg-black/20 hover:border-white/25"}`}
                    >
                      <img src={fileUrl(c.image_path)} alt={c.name} className="h-7 w-7 rounded-full object-cover" />
                      <span className="text-xs font-medium">{c.name}</span>
                      {on && <Check className="h-3.5 w-3.5 text-[#FF3B60]" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold mb-2 block">Length</label>
              <div className="flex gap-2">
                {[4, 5, 6].map((n) => (
                  <button
                    key={n} onClick={() => setNumBeats(n)}
                    data-testid={`beats-${n}`}
                    className={`flex-1 rounded-xl py-2.5 text-sm font-medium border transition-all ${numBeats === n ? "candy-strawberry text-white border-transparent" : "border-white/10 bg-black/20 text-slate-300 hover:border-white/25"}`}
                  >
                    {n} clips<span className="block text-[10px] opacity-70">~{n * 8}s</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold mb-2 block">Story brain</label>
              <select
                data-testid="model-select"
                value={model} onChange={(e) => setModel(e.target.value)}
                className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-[#FF3B60]"
              >
                {models.map((m) => <option key={m.id} value={m.id} className="bg-[#161522]">{m.label}</option>)}
              </select>
            </div>
          </div>

          <button
            onClick={draft} disabled={drafting}
            data-testid="generate-script-btn"
            className="w-full candy-strawberry rounded-xl py-3.5 font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {drafting ? "Writing your script…" : "Draft the script"}
          </button>
        </div>
      </div>
    );
  }

  // ---------- REVIEW / RENDER ----------
  const sIdx = stageIndex(story.status);
  const progress = story.status === "done" ? 100
    : story.status === "stitching" ? 90
    : story.status === "regenerating" ? 55
    : story.status === "rendering" ? Math.round((story.current_clip / Math.max(1, story.total_clips)) * 80) + 5
    : story.status === "queued" ? 5 : 0;
  const isLive = ["queued", "rendering", "stitching", "regenerating"].includes(story.status);
  const editable = story.status === "draft" || story.status === "failed";

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8">
      {/* left: script */}
      <div>
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            {editable ? (
              <input
                value={story.title} onChange={(e) => setStory({ ...story, title: e.target.value })}
                className="font-display text-2xl font-bold bg-transparent border-b border-transparent hover:border-white/20 focus:border-[#FF3B60] outline-none"
                data-testid="story-title-input"
              />
            ) : (
              <h2 className="font-display text-2xl font-bold" data-testid="story-title">{story.title}</h2>
            )}
            <p className="text-sm text-[#FF8A00] mt-1" data-testid="story-hook">{story.hook}</p>
          </div>
          <button onClick={reset} className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5" data-testid="new-story-reset">
            <RotateCcw className="h-3.5 w-3.5" /> Start over
          </button>
        </div>

        <div className="space-y-3">
          {story.beats.map((b, i) => (
            <motion.div
              key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
              className="glass rounded-2xl p-4" data-testid={`script-beat-card-${i + 1}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 shrink-0 rounded-lg candy-blueberry grid place-items-center text-sm font-bold text-white">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  {editable ? (
                    <>
                      <input value={b.scene_title} onChange={(e) => editBeat(i, "scene_title", e.target.value)}
                        className="font-semibold text-sm bg-transparent w-full outline-none focus:text-[#FF3B60]" placeholder="Scene title" />
                      <textarea value={b.action} onChange={(e) => editBeat(i, "action", e.target.value)} rows={2}
                        className="mt-1 w-full text-xs text-slate-300 bg-black/20 rounded-lg p-2 outline-none focus:border-[#FF3B60] border border-transparent resize-none" placeholder="Visual action" />
                      <input value={b.dialogue} onChange={(e) => editBeat(i, "dialogue", e.target.value)}
                        className="mt-1.5 w-full text-xs italic text-[#FFD166] bg-transparent outline-none" placeholder="Dialogue line" />
                    </>
                  ) : (
                    <>
                      <h4 className="font-semibold text-sm">{b.scene_title}</h4>
                      <p className="text-xs text-slate-300 mt-1">{b.action}</p>
                      {b.dialogue && <p className="text-xs italic text-[#FFD166] mt-1.5">“{b.dialogue}”</p>}
                    </>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2 font-mono-studio text-[10px] text-slate-400">
                    {b.mood && <span className="px-2 py-0.5 rounded bg-white/5">mood: {b.mood}</span>}
                    {b.camera && <span className="px-2 py-0.5 rounded bg-white/5">cam: {b.camera}</span>}
                    {b.sfx && <span className="px-2 py-0.5 rounded bg-white/5">sfx: {b.sfx}</span>}
                  </div>
                </div>
                {(story.status === "done" || story.status === "regenerating") && (
                  <button
                    onClick={() => regenerateClip(i)}
                    disabled={isLive}
                    data-testid={`redo-clip-${i + 1}`}
                    title="Re-roll just this clip"
                    className="shrink-0 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium border border-white/15 text-slate-300 hover:border-[#FF3B60] hover:text-white transition-colors disabled:opacity-50"
                  >
                    {story.status === "regenerating" && story.regen_index === i
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <RotateCcw className="h-3.5 w-3.5" />}
                    Redo
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {editable && (
          <div className="flex gap-3 mt-5">
            <button onClick={saveBeats} className="rounded-xl px-4 py-2.5 text-sm font-medium border border-white/15 hover:bg-white/5 flex items-center gap-2" data-testid="save-script-btn">
              <Pencil className="h-3.5 w-3.5" /> Save edits
            </button>
          </div>
        )}
      </div>

      {/* right: preview + controls */}
      <div className="space-y-5">
        <VerticalVideo path={story.final_path} version={story.updated_at} badge={story.status === "done" ? "9:16 · HD" : "9:16"} />

        {/* pipeline */}
        <div className="glass rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">Pipeline</span>
            <span className="text-xs font-mono-studio text-slate-400" data-testid="pipeline-stage">{story.stage}</span>
          </div>
          <div className="h-2 rounded-full bg-black/40 overflow-hidden" data-testid="pipeline-progress-bar">
            <motion.div className="h-full candy-strawberry" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
          </div>
          <div className="flex justify-between mt-3">
            {STAGES.map((st, i) => (
              <div key={st} className="flex flex-col items-center gap-1">
                <div className={`h-2.5 w-2.5 rounded-full ${i <= sIdx ? "bg-[#2ECC71]" : "bg-white/15"}`} />
                <span className={`text-[9px] ${i <= sIdx ? "text-slate-300" : "text-slate-600"}`}>{st}</span>
              </div>
            ))}
          </div>
          {isLive && (
            <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Each 8s clip takes 30–120s. Hang tight.
            </p>
          )}
          {story.status === "failed" && (
            <p className="text-[11px] text-[#FF7675] mt-3 flex items-start gap-1.5" data-testid="render-error">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {story.error}
            </p>
          )}
          {story.status === "done" && story.error && (
            <p className="text-[11px] text-[#FFD166] mt-3 flex items-start gap-1.5" data-testid="reroll-error">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {story.error}
            </p>
          )}
        </div>

        {/* options */}
        <div className="glass rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm flex items-center gap-2"><Captions className="h-4 w-4 text-[#38BDF8]" /> Baked captions</span>
            <button
              disabled={!editable}
              onClick={() => setStory({ ...story, captions: !story.captions })}
              data-testid="captions-toggle"
              className={`h-6 w-11 rounded-full transition-colors relative ${story.captions ? "bg-[#2ECC71]" : "bg-white/15"} disabled:opacity-50`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${story.captions ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div>
            <span className="text-sm flex items-center gap-2 mb-2"><Music2 className="h-4 w-4 text-[#A855F7]" /> Music bed</span>
            <div className="grid grid-cols-3 gap-2">
              {music.map((m) => (
                <button
                  key={m.id} disabled={!editable}
                  onClick={() => setStory({ ...story, music_id: m.id })}
                  data-testid={`music-${m.id}`}
                  className={`rounded-lg py-2 text-xs font-medium border transition-all disabled:opacity-60 ${story.music_id === m.id ? "border-[#A855F7] bg-[#A855F7]/15 text-white" : "border-white/10 text-slate-300 hover:border-white/25"}`}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* actions */}
        {story.status === "done" ? (
          <div className="space-y-2">
            <button onClick={() => downloadFile(story.final_path, `${story.title}.mp4`)}
              className="w-full candy-lime rounded-xl py-3 font-semibold text-[#0F0E17] flex items-center justify-center gap-2 hover:opacity-90" data-testid="download-mp4-btn">
              <Download className="h-4 w-4" /> Download MP4
            </button>
            <button onClick={goEpisodes} className="w-full rounded-xl py-2.5 text-sm font-medium border border-white/15 hover:bg-white/5 flex items-center justify-center gap-2" data-testid="view-episodes-btn">
              View in Episodes <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={startRender} disabled={isLive}
            data-testid="start-rendering-btn"
            className="w-full candy-strawberry rounded-xl py-3.5 font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {isLive ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clapperboard className="h-4 w-4" />}
            {isLive ? "Rendering…" : story.status === "failed" ? "Retry render" : "Generate video"}
          </button>
        )}
      </div>
    </div>
  );
}
